import { z } from 'zod';

export const IdentifierSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/, 'Use letters, numbers, dots, underscores, colons, or hyphens.');

export const NonEmptyTextSchema = z.string().trim().min(1).max(500);

export const IsoDateTimeSchema = z.string().datetime({ offset: true });

export const LocalizedTextSchema = z
  .object({
    de: NonEmptyTextSchema,
    en: NonEmptyTextSchema.optional(),
    cs: NonEmptyTextSchema.optional(),
  })
  .strict();

export const LongitudeSchema = z.number().finite().min(-180).max(180);
export const LatitudeSchema = z.number().finite().min(-90).max(90);
export const PositionSchema = z.tuple([LongitudeSchema, LatitudeSchema]);

export const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a six-digit hexadecimal color.');

export const GeoJsonPointGeometrySchema = z
  .object({
    type: z.literal('Point'),
    coordinates: PositionSchema,
  })
  .strict();

export const GeoJsonLineStringGeometrySchema = z
  .object({
    type: z.literal('LineString'),
    coordinates: z.array(PositionSchema).min(2),
  })
  .strict();

export const GeoJsonPolygonGeometrySchema = z
  .object({
    type: z.literal('Polygon'),
    coordinates: z.array(z.array(PositionSchema).min(4)).min(1),
  })
  .strict();

export const GeoJsonGeometrySchema = z.discriminatedUnion('type', [
  GeoJsonPointGeometrySchema,
  GeoJsonLineStringGeometrySchema,
  GeoJsonPolygonGeometrySchema,
]);

export type Identifier = z.infer<typeof IdentifierSchema>;
export type LocalizedText = z.infer<typeof LocalizedTextSchema>;
export type Position = z.infer<typeof PositionSchema>;
export type GeoJsonPointGeometry = z.infer<typeof GeoJsonPointGeometrySchema>;
export type GeoJsonLineStringGeometry = z.infer<typeof GeoJsonLineStringGeometrySchema>;
export type GeoJsonPolygonGeometry = z.infer<typeof GeoJsonPolygonGeometrySchema>;
export type GeoJsonGeometry = z.infer<typeof GeoJsonGeometrySchema>;
